import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  EnterpriseTeam,
  EnterpriseWorkspace
} from 'v5/api/enterprise/enterprise.api';
import { Button } from 'v5/design-system/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from 'v5/design-system/Dialog';
import { EnterpriseTeamPicker } from 'v5/features/core/enterprise/EnterpriseTeamPicker';
import { EnterpriseWorkspacePicker } from 'v5/features/core/enterprise/EnterpriseWorkspacePicker';
import { MountElementProps } from 'v5/messages/mount-element';
import { WithServices } from 'v5/platform/ts/services';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import {
  TeamTriggerContent,
  WorkspaceTriggerContent
} from './TransferToTeamDialogContent';

export type ChooseTeamDialogProps = MountElementProps &
  WithServices<[WithEnterpriseService]> & {
    organizationId: string;
    /** The opener's workspace, picked in advance; its team is still to choose. */
    initialWorkspace: EnterpriseWorkspace | null;
    titleMessage: string;
    descriptionMessage: string;
    onChoose: (choice: {
      workspace: EnterpriseWorkspace;
      team: EnterpriseTeam;
    }) => void;
  };

/**
 * A step before a create that only a team can hold: pick the workspace and
 * the team, then the host opens its own dialog in that team.
 */
export function ChooseTeamDialog({
  open,
  onOpenChange,
  organizationId,
  initialWorkspace,
  titleMessage,
  descriptionMessage,
  onChoose,
  services
}: ChooseTeamDialogProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ChooseTeamDialog' });
  const [workspace, setWorkspace] = useState(initialWorkspace);
  const [team, setTeam] = useState<EnterpriseTeam | null>(null);
  const teamTrigger = useRef<HTMLButtonElement>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-120 p-0"
        onOpenAutoFocus={event => {
          // With the opener's workspace picked, the team is the next choice.
          if (!initialWorkspace) return;
          event.preventDefault();
          teamTrigger.current?.focus();
        }}
      >
        <form
          className="flex flex-col"
          onSubmit={event => {
            event.preventDefault();
            if (workspace && team) onChoose({ workspace, team });
          }}
        >
          <DialogHeader>
            <DialogTitle>{titleMessage}</DialogTitle>
            <DialogDescription className="px-6 pt-4">
              {descriptionMessage}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 px-6 py-4">
            <label className="flex flex-col gap-2 text-sm font-medium">
              {t('workspace')}
              <EnterpriseWorkspacePicker
                organizationId={organizationId}
                services={services}
                onWorkspaceSelect={next => {
                  setWorkspace(next);
                  setTeam(null);
                }}
              >
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="w-full justify-start font-normal"
                >
                  <WorkspaceTriggerContent
                    workspace={workspace}
                    placeholder={t('selectWorkspace')}
                  />
                </Button>
              </EnterpriseWorkspacePicker>
            </label>
            {workspace ? (
              <label className="flex flex-col gap-2 text-sm font-medium">
                {t('team')}
                <EnterpriseTeamPicker
                  organizationId={organizationId}
                  workspaceId={workspace.id}
                  services={services}
                  onTeamSelect={setTeam}
                >
                  <Button
                    ref={teamTrigger}
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="w-full justify-start font-normal"
                  >
                    <TeamTriggerContent
                      team={team}
                      placeholder={t('selectTeam')}
                    />
                  </Button>
                </EnterpriseTeamPicker>
              </label>
            ) : null}
          </div>
          <DialogFooter className="border-t border-border-default px-6 py-3">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!workspace || !team}
            >
              {t('continue')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
